import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Button,
  IconButton,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import ChatIcon from '@mui/icons-material/Chat';
import SendIcon from '@mui/icons-material/Send';
import CloseIcon from '@mui/icons-material/Close';
import envDetective from 'environment-detective'

const ChatBox = () => {
  const [open, setOpen] = useState(false);
  const [userMessage, setUserMessage] = useState('');
  const [messages, setMessages] = useState([]);

  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (open) {
      setMessages([]);
    }
  }, [open]);

  const handleSend = (msg) => {
    const messageToSend = msg || userMessage;
    if (!messageToSend.trim()) return;

    const newMessage = { sender: 'user', text: messageToSend };
    setMessages((prev) => [...prev, newMessage]);

    const aiResponse = {
      sender: 'ai',
      text: generateResponse(messageToSend),
    };

    setTimeout(() => {
      setMessages((prev) => [...prev, aiResponse]);
    }, 500);

    setUserMessage('');
  };

  const generateResponse = (msg) => {
    const lowerMsg = msg.toLowerCase();

    if (["hi", "hello", "hey"].some((greet) => lowerMsg.includes(greet))) {
      return "Hi there! 👋 Welcome to Film-hook. How can I assist you today?";
    }

    if (lowerMsg.includes("what is") || lowerMsg.includes("about")) {
      return "Film-hook is a platform for filmmakers, artists, and creatives to showcase their work, connect with clients, and grow their network. 🎬";
    }

    if (lowerMsg.includes("feature") || lowerMsg.includes("benefit")) {
      return `Here are some top features of Film-hook:\n✅ Upload and display your portfolio\n✅ Connects Anyone to Anywhere\n✅ Promote your creative work\n✅ Collaborate with others\nWould you like a walkthrough?`;
    }

    if (lowerMsg.includes("register") || lowerMsg.includes("signup")) {
      return "To register on Film-hook, just head to our app or website and click on 'Sign Up'. It only takes a minute to join the creative network!";
    }

    if (lowerMsg.includes("login")) {
      return "To log in, open the Film-hook app or visit our website. Use your registered email and password. Forgot your password? No worries – there's a reset option.";
    }

    if (lowerMsg.includes("download") || lowerMsg.includes("app")) {
      return "Download the Filmhook app:\n🤖 Google Play (Android): https://play.google.com/store/apps/details?id=com.filmhookapps&hl=en_IN\n🍎 Apple App Store (iOS): https://apps.apple.com/us/app/filmhook/id6793529515";
    }

    if (lowerMsg.includes("contact")) {
      return `📍 Address: 2nd Floor, 'A, Mysore Rd, Bengaluru\n📞 Phone: +91 77957 20922\n📧 Email: support@filmhookapps.com\nWe're here to help!`;
    }

    if (lowerMsg.includes("support") || lowerMsg.includes("help")) {
      return "Need help? Reach out to our support team at support@film-hookapps.com or call us directly at +91 77957 20922.";
    }

    if (lowerMsg.includes("price") || lowerMsg.includes("cost") || lowerMsg.includes("free")) {
      return "Film-hook offers both free and premium plans. The basic version is free to use. For advanced features, check our premium plans inside the app.";
    }

    if (lowerMsg.includes("career") || lowerMsg.includes("job")) {
      return "We're always looking for passionate people! If you’re interested in joining the Film-hook team, email us at hr@film-hookapps.com.";
    }

    if (lowerMsg.includes("location") || lowerMsg.includes("office")) {
      return "We are located at 2nd Floor, 'A, Mysore Road, Bengaluru, Karnataka, India. Drop by if you're nearby! 😊";
    }

    if (lowerMsg.includes("privacy")) {
      return "You can read our privacy policy at: https://film-hookapps.com/privacy-policy";
    }

    if (lowerMsg.includes("terms") || lowerMsg.includes("conditions")) {
      return "Please review our terms and conditions here: https://film-hookapps.com/terms-and-conditions";
    }

    if (lowerMsg.includes("collaborate") || lowerMsg.includes("partner")) {
      return "Interested in collaborating or partnering with us? Email our business team at connect@film-hookapps.com.";
    }
    if (lowerMsg.includes("thank you") || lowerMsg.includes("thanks") || lowerMsg.includes("thankyou")) {
  return "You're most welcome! 😊 If you have more questions or need help, feel free to ask anytime.";
}

    if (lowerMsg.includes("social") || lowerMsg.includes("instagram") || lowerMsg.includes("facebook")) {
      return `Follow us on:\n📸 Instagram: https://instagram.com/filmhook\n👍 Facebook: https://facebook.com/filmhook\n🐦 Twitter: https://twitter.com/filmhookapp`;
    }

    return "I'm here to assist you with Film-hook related queries. Try asking about features, support, contact info, careers, or how to get started! 😊";
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 20,
        right: 20,
        zIndex: 9999,
      }}
    >
      {!open ? (
        <IconButton
          onClick={() => setOpen(true)}
          sx={{
            backgroundColor: 'black',
            color: '#fff',
            p: 2,
            '&:hover': {
              backgroundColor: 'black',
            },
          }}
        >
          <ChatIcon />
        </IconButton>
      ) : (
        <Paper
          elevation={6}
          sx={{
            width: 320,
            maxHeight: 450,
            height: "400px",
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: '#fff',
            position: 'relative',
          }}
        >
          {/* Smaller Close Button */}
          <IconButton
            onClick={() => setOpen(false)}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              color: 'black',
              width: 30,
              height: 30,
            }}
          >
            <CloseIcon sx={{ fontSize: 20, fontWeight: "bold" }} />
          </IconButton>

          <Typography variant="h6" gutterBottom sx={{ pr: 4 }}>
            Chat With Us
          </Typography>

          {/* Quick option buttons */}
          <Box
            sx={{
              mb: 1,
              display: 'flex',
              gap: 1,
              flexWrap: 'wrap',
            }}
          >
            {['Hi', 'About', 'Contact', 'Thank You!!'].map((option) => (
              <Button
                key={option}
                variant="outlined"
                size="small"
                onClick={() => {
                  setUserMessage(option);
                  handleSend(option);
                }}
                sx={{ textTransform: 'none' }}
              >
                {option}
              </Button>
            ))}
          </Box>

          <Box
            sx={{
              flex: 1,
              overflowY: 'auto',
              mb: 1,
              px: 1,
              bgcolor: '#f5f5f5',
              borderRadius: 1,
            }}
          >
            {messages.map((msg, i) => (
              <Box
                key={i}
                sx={{
                  textAlign: msg.sender === 'user' ? 'right' : 'left',
                  mb: 1,
                }}
              >
                <Typography
                  sx={{
                    display: 'inline-block',
                    px: 1.5,
                    py: 1,
                    borderRadius: 2,
                    color: msg.sender === 'user' ? 'black' : 'white',
                    backgroundColor: msg.sender === 'user' ? 'white' : '#1e88e5',
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  {msg.text}
                </Typography>
              </Box>
            ))}
            <div ref={messagesEndRef} />
          </Box>

          <Box display="flex" gap={1}>
            <TextField
              fullWidth
              size="small"
              placeholder="Type a message..."
              value={userMessage}
              onChange={(e) => setUserMessage(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: 'black',
                  },
                  '&:hover fieldset': {
                    borderColor: 'black',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: 'black',
                  },
                },
              }}
            />
            <IconButton onClick={() => handleSend()}>
              <SendIcon />
            </IconButton>
          </Box>
        <div>Browser: {envDetective.getBrowser()}</div>
        <div>{envDetective.getNodeVersion()}</div>
        </Paper>
        
      )}
    </Box>
  );
};

export default ChatBox;
